/**
 * @file ExpandingSearch.tsx
 * @brief The Notes screen's search, for every page: a round glass button that
 *        opens into a search pill.
 *
 * @details
 * DES (01-10-2026), on Diary's always-open grey search bar: "yeh jo searches
 * hain should be like we have on note screen". Lifted from NotesPage's dock:
 * - Closed: a 56px glass circle with the search icon.
 * - Click: it widens (300ms) into a pill with the input focused.
 * - Blur while empty, or Escape, closes it again (Escape also clears).
 * - A ✕ clears the text without closing.
 * - It opens on its own when it mounts with a value (from the URL), so a
 *   reload with `?q=` shows what is being searched.
 *
 * Controlled: the page keeps the value (URL param or state) and its own
 * debounce, if any.
 *
 * @author DES
 * @date 01-10-2026
 * @updated 08-10-2026 — `alwaysOpen` (the Boardrooms dock's wide field)
 */

import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import { dockGlass } from "@/shared/theme/dockGlass";

/** The shared dock glass (shared/theme/dockGlass.ts). */
export const searchGlass = dockGlass;

export function ExpandingSearch({
  value,
  onChange,
  placeholder,
  label = "Search",
  className,
  openWidth = "w-[min(22rem,70vw)]",
  alwaysOpen = false,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  /** Accessible name of the closed button. */
  label?: string;
  className?: string;
  /** Tailwind width class when open. */
  openWidth?: string;
  /**
   * A wide field from the start that never closes to the circle (Boardrooms
   * dock, DES 08-10-2026: search as wide as New boardroom was, "+" round).
   */
  alwaysOpen?: boolean;
}) {
  const [openState, setOpen] = useState(Boolean(value));
  const open = alwaysOpen || openState;
  const inputRef = useRef<HTMLInputElement | null>(null);

  // A value set from outside (URL, a "clear filters" elsewhere) opens it.
  useEffect(() => {
    if (value) setOpen(true);
  }, [value]);

  return (
    <div
      className={cn(
        searchGlass,
        "flex h-14 shrink-0 items-center overflow-hidden transition-[width] duration-300",
        open ? openWidth : "w-14",
        className,
      )}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => {
          setOpen(true);
          window.setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className="flex h-14 w-14 shrink-0 items-center justify-center focus-visible:outline-none"
      >
        <Search className="h-6 w-6 text-foreground" strokeWidth={1.5} />
      </button>
      {open ? (
        <>
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => {
              if (!value.trim()) setOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                onChange("");
                setOpen(false);
              }
            }}
            placeholder={placeholder}
            aria-label={label}
            className="min-w-0 flex-1 bg-transparent pr-2 text-[15px] outline-none placeholder:text-muted-foreground"
          />
          {value ? (
            <button
              type="button"
              aria-label="Clear search"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onChange("")}
              className="mr-3 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
